import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Icon } from './JournalOverview';
import { MAX_ARCHIVE_SELECTION } from './archive-store';
import useMenuTabExit from './useMenuTabExit';
import './journal-gallery.css';
import {FlowSurface} from './FlowMotion';

const imageSource = image => {
  if (typeof image?.src !== 'string' || !image.src.trim()) return null;
  const src = image.src.trim();
  if (/^data:/i.test(src)) return /^data:image\//i.test(src) ? src : null;
  if (/^[a-z][a-z\d+.-]*:/i.test(src) && !/^(https?|blob):/i.test(src)) return null;
  return src;
};

function galleryImages(trade) {
  const candidates = [trade?.mainChart, ...(Array.isArray(trade?.images) ? trade.images : [])];
  const seen = new Set();
  return candidates.flatMap(image => {
    const src = imageSource(image);
    if (!src || seen.has(src)) return [];
    seen.add(src);
    return [{ src, label: typeof image.label === 'string' && image.label.trim() ? image.label : image === trade?.mainChart ? 'Main chart' : 'Trade chart' }];
  });
}

export function tradeGalleryImage(trade) {
  return galleryImages(trade)[0] || null;
}

export function galleryReturn(trade) {
  const value = typeof trade?.returnPct === 'number' ? trade.returnPct : typeof trade?.returnPct === 'string' && trade.returnPct.trim() ? Number(trade.returnPct) : NaN;
  if (!Number.isFinite(value)) return { value: '—', label: 'Return not recorded', tone: 'neutral' };
  return { value: `${value > 0 ? '+' : value < 0 ? '−' : ''}${Math.abs(value).toFixed(2)}%`, label: 'Return', tone: value > 0 ? 'positive' : value < 0 ? 'negative' : 'neutral' };
}

function formattedReturn(trade, formatter) {
  const fallback = galleryReturn(trade);
  if (typeof formatter !== 'function') return fallback;
  try {
    const supplied = formatter(trade);
    if (!supplied || typeof supplied.value !== 'string' || !supplied.value.trim()) return fallback;
    return { value: supplied.value, label: typeof supplied.label === 'string' && supplied.label.trim() ? supplied.label : 'Return', tone: ['positive', 'negative', 'neutral'].includes(supplied.tone) ? supplied.tone : fallback.tone };
  } catch { return fallback; }
}

function galleryDate(value) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return 'Date not set';
  const date = new Date(`${value}T12:00:00`);
  if (!Number.isFinite(date.getTime()) || date.getFullYear() !== Number(value.slice(0, 4)) || date.getMonth() + 1 !== Number(value.slice(5, 7)) || date.getDate() !== Number(value.slice(8, 10))) return 'Date not set';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function MissingChart({ unavailable = false }) {
  return <span className="tl-gallery-placeholder">
    <svg viewBox="0 0 88 54" width="88" height="54" fill="none" aria-hidden="true">
      <path d="M4 14h80M4 27h80M4 40h80" className="tl-gallery-placeholder-grid"/>
      <path d="M16 18v25m14-32v25m14-30v26m14-17v27m14-32v27" stroke="currentColor" strokeWidth="1.3"/>
      <rect x="12" y="25" width="8" height="10" rx="1"/><rect x="26" y="16" width="8" height="12" rx="1"/><rect x="40" y="12" width="8" height="11" rx="1"/><rect x="54" y="24" width="8" height="9" rx="1"/><rect x="68" y="16" width="8" height="12" rx="1"/>
    </svg>
    <span>{unavailable ? 'Chart unavailable' : 'No chart added'}</span>
    <small>Your trade details are still here</small>
  </span>;
}

function TradeActions({ trade, onEdit, onDelete, onDuplicate, onTogglePublic, onArchive, onRestore, archived }) {
  const [open, setOpen] = useState(false);
  const trigger = useRef(null), panel = useRef(null), container = useRef(null);
  const tabExit = useMenuTabExit(open,container,()=>setOpen(false));
  const id = useId();
  const actions = [
    onEdit && { label: 'Edit trade', icon: 'edit', action: () => onEdit(trade) },
    onDuplicate && { label: 'Duplicate trade', icon: 'copy', action: () => onDuplicate(trade) },
    !archived && onTogglePublic && { label: trade.isPublic ? 'Make private' : 'Make public', icon: trade.isPublic ? 'lock' : 'globe', action: () => onTogglePublic(trade) },
    onArchive && { label: archived ? 'Move to another folder' : 'Move to archive', icon: 'archive', action: () => onArchive(trade) },
    archived && onRestore && { label: 'Restore to current journal', icon: 'history', action: () => onRestore(trade) },
    onDelete && { label: 'Delete trade', icon: 'trash', danger: true, action: () => onDelete(trade.id) },
  ].filter(Boolean);
  useEffect(() => {
    if (!open) return;
    panel.current?.querySelector('button')?.focus({preventScroll:true});
    const outside = event => { if (!container.current?.contains(event.target)) setOpen(false); };
    document.addEventListener('pointerdown', outside);
    return () => document.removeEventListener('pointerdown', outside);
  }, [open]);
  if (!actions.length) return null;
  const key = event => {
    if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); setOpen(false); trigger.current?.focus({preventScroll:true}); return; }
    const buttons = [...(panel.current?.querySelectorAll('button') || [])];
    const current = buttons.indexOf(document.activeElement);
    if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key) && buttons.length) {
      event.preventDefault();
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 : (current + (event.key === 'ArrowUp' ? -1 : 1) + buttons.length) % buttons.length;
      buttons[next]?.focus();
    }
  };
  return <div className="tl-gallery-more" ref={container} onKeyDownCapture={tabExit}>
    <button ref={trigger} type="button" className="tl-gallery-action tl-gallery-more-trigger" aria-label={`More actions for ${trade.instrument || 'untitled'} trade`} aria-haspopup="menu" aria-expanded={open} aria-controls={open ? id : undefined} onClick={() => setOpen(value => !value)} onKeyDown={event => { if (event.key === 'ArrowDown') { event.preventDefault(); setOpen(true); } }}><Icon name="more" size={19}/></button>
    {open && <div ref={panel} id={id} role="menu" aria-label={`${trade.instrument || 'Trade'} actions`} className="tl-gallery-menu" data-swipe-ignore data-tl-popover onKeyDown={key}>
      {actions.map(item => <button type="button" role="menuitem" key={item.label} className={item.danger ? 'is-danger' : ''} onClick={() => { setOpen(false); trigger.current?.focus({preventScroll:true}); item.action(); }}><Icon name={item.icon} size={16}/>{item.label}</button>)}
    </div>}
  </div>;
}

function GalleryTrade({ trade, account, onView, onCard, selection, formatReturn, visibilityPending, ...actions }) {
  const images = galleryImages(trade), image = images[0];
  const [failedSource, setFailedSource] = useState(null);
  const visibleImage = image && failedSource !== image.src;
  const selecting = !!selection?.enabled;
  const selected = !!selection?.selected?.has(trade.id);
  const selectionFull = selecting && !selected && (selection?.selected?.size || 0) >= MAX_ARCHIVE_SELECTION;
  const result = ['WIN', 'LOSS', 'BE', 'OPEN'].includes(trade.result) ? trade.result : 'OPEN';
  const resultLabel = { WIN: 'Win', LOSS: 'Loss', BE: 'Break even', OPEN: 'Open' }[result];
  const value = formattedReturn(trade, formatReturn);
  const returnTone = value.tone;
  const instrument = trade.instrument || 'Untitled trade';
  const selectLabel = `Select ${instrument} trade on ${trade.date || 'unknown date'}`;
  const view = () => { if (selecting) selection?.toggle?.(trade.id); else onView?.(trade); };
  return <FlowSurface as="article" className={`tl-gallery-trade${selected ? ' is-selected' : ''}`} data-result={result} aria-label={`${instrument} trade on ${trade.date || 'unknown date'}`}>
    <div className="tl-gallery-chart-wrap">
      <button type="button" className="tl-gallery-chart" aria-label={selecting ? `Select ${instrument} trade card` : `View ${instrument} chart and trade details`} aria-pressed={selecting ? selected : undefined} disabled={selectionFull || (!selecting && !onView)} onClick={view} data-swipe-media>
        {visibleImage ? <img src={image.src} alt={`${instrument}: ${image.label}`} loading="lazy" decoding="async" onError={() => setFailedSource(image.src)}/> : <MissingChart unavailable={!!image}/>}
        {visibleImage && <span className="tl-gallery-chart-caption"><Icon name="eye" size={14}/>{selecting ? 'Select trade' : 'Open chart & details'}</span>}
      </button>
      {selecting && <label className="tl-gallery-select"><input type="checkbox" aria-label={selectLabel} checked={selected} disabled={selectionFull} onChange={() => selection?.toggle?.(trade.id)}/></label>}
      <span className={`tl-gallery-result is-${result.toLowerCase()}`}><span aria-hidden="true"/>{resultLabel}</span>
      {images.length > 1 && <span className="tl-gallery-image-count"><Icon name="layers" size={12}/>{images.length} charts</span>}
    </div>
    <div className="tl-gallery-body">
      <div className="tl-gallery-heading">
        <div><button type="button" onClick={() => onView?.(trade)} className="tl-gallery-instrument" disabled={!onView} aria-label={`View ${instrument} trade`}>{instrument}</button><div className="tl-gallery-trade-meta">{trade.direction && <span className={trade.direction === 'LONG' ? 'is-long' : trade.direction === 'SHORT' ? 'is-short' : ''}>{trade.direction}</span>}{trade.timeframe && <span>{trade.timeframe}</span>}{account?.name && <span className="tl-gallery-account" title={account.name}>{account.name}</span>}</div></div>
        <div className="tl-gallery-return"><span>{value.label || 'Return'}</span><strong className={`is-${returnTone}`}>{value.value}</strong></div>
      </div>
      <div className="tl-gallery-context"><span><Icon name="calendar" size={13}/>{galleryDate(trade.date)}</span>{trade.session && <span><Icon name="clock" size={13}/>{trade.session}</span>}</div>
      {((trade.rr !== '' && trade.rr != null) || trade.setup) && <div className="tl-gallery-notes">{trade.setup && <span title={trade.setup}>{trade.setup}</span>}{trade.rr !== '' && trade.rr != null && <span className="tl-gallery-rr">R:R <b>1:{trade.rr}</b></span>}</div>}
    </div>
    <footer className="tl-gallery-footer"><button type="button" className="tl-gallery-action" onClick={() => onView?.(trade)} disabled={!onView} aria-label={`Open ${instrument} trade`}><Icon name="book" size={15}/>Open trade</button>{onCard && <button type="button" className="tl-gallery-action" onClick={() => onCard(trade)} aria-label={`Share studio for ${instrument} trade`}><Icon name="report" size={15}/>Share studio</button>}{visibilityPending?.has(trade.id)?<span className="tl-gallery-saving" role="status">Saving visibility…</span>:<TradeActions trade={trade} {...actions}/>}</footer>
  </FlowSurface>;
}

export default function JournalGallery({ trades = [], accounts = [], accountMode = true, ...props }) {
  const accountMap = useMemo(() => new Map(accounts.map(account => [account.id, account])), [accounts]);
  return <section className="tl-journal-gallery" aria-label="Trade gallery">{trades.map(trade => <GalleryTrade key={trade.id} trade={trade} account={accountMode ? accountMap.get(trade.accountId) : null} {...props}/>)}</section>;
}
